import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Smartphone, Apple, Check, LogIn, AlertTriangle, ExternalLink, Send, Zap } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useReferralCode } from "../hooks/useReferralCode";
import { ShareBeta } from "./ShareBeta";
import {
    onInstallAvailability,
    triggerInstallPrompt,
    isStandalone,
} from "../serviceWorkerRegistration";

const TESTFLIGHT_URL = process.env.REACT_APP_TESTFLIGHT_URL || "";
const PLAY_STORE_URL = process.env.REACT_APP_PLAY_STORE_URL || "";

/** Normalise an Axios error into a string we can render in JSX.
 *  FastAPI/Pydantic returns 422 with `detail` as an array of objects — rendering
 *  that array directly throws "Objects are not valid as a React child" and
 *  unmounts the whole app. */
const formatApiError = (err, fallback = "Couldn't add you. Try again.") => {
    const d = err?.response?.data?.detail;
    if (Array.isArray(d)) return d[0]?.msg || "Invalid email.";
    if (typeof d === "string") return d;
    return fallback;
};

const _GoogleGlyph = () => (
    <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
        <path fill="currentColor" d="M5.26 14.5l-.85 3.18-3.12.07A11 11 0 0 1 0 12c0-1.84.45-3.58 1.26-5.11l2.78.51 1.22 2.77a6.55 6.55 0 0 0 0 3.66z"/>
        <path fill="currentColor" d="M21.78 9.78c.14.74.22 1.51.22 2.22 0 .87-.09 1.71-.27 2.52a11 11 0 0 1-3.87 6.28L14.6 20.6l-.55-2.78a6.55 6.55 0 0 0 2.82-3.34h-5.21v-4.7h10.12z"/>
        <path fill="currentColor" d="M17.86 20.8A11 11 0 0 1 12 23a11 11 0 0 1-9.82-5.93l3.97-3.25a6.55 6.55 0 0 0 9.39 3.36l2.32 3.62z"/>
        <path fill="currentColor" d="M18.02 3.27l-3.97 3.25a6.55 6.55 0 0 0-9.6 3.43l-3.99-3.25A11 11 0 0 1 12 1c2.32 0 4.46.71 6.02 2.27z"/>
    </svg>
);

/**
 * Smart "Download the app" modal — adapts to four states:
 *  1. Anonymous: show inline waitlist signup form
 *  2. Signed-in, NOT on list: one-click join using their account email
 *  3. Signed-in, status='pending': show queue position + "we'll email you"
 *  4. Signed-in, status='invited': show TestFlight + Play Store buttons
 *
 * If TESTFLIGHT_URL / PLAY_STORE_URL aren't configured yet (closed beta not
 * launched), even invited users see a "Beta opens soon" panel — we won't
 * surface a broken link.
 */
export const DownloadAppModal = ({ onClose }) => {
    const { user } = useAuth();
    const [meStatus, setMeStatus] = useState(null);   // null | {status, position}
    const [loading, setLoading] = useState(true);
    const [joining, setJoining] = useState(false);
    const [error, setError] = useState(null);
    const [guestEmail, setGuestEmail] = useState("");
    const { code: referredBy, referrer, clear: clearReferral } = useReferralCode();
    // PWA install state. `null` = unknown, `false` = unavailable on this browser, `true` = ready to prompt.
    const [pwaInstallAvailable, setPwaInstallAvailable] = useState(false);
    const [pwaInstalled, setPwaInstalled] = useState(false);
    const _isIos = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const _alreadyStandalone = isStandalone();

    useEffect(() => {
        if (_alreadyStandalone) return;
        const off = onInstallAvailability((ready) => setPwaInstallAvailable(ready));
        return off;
    }, [_alreadyStandalone]);

    const installPwa = async () => {
        const choice = await triggerInstallPrompt();
        if (choice.outcome === "accepted") setPwaInstalled(true);
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/waitlist/me");
                if (!cancelled) setMeStatus(data);
            } catch {
                if (!cancelled) setMeStatus({ status: null });
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [user]);

    // Lock body scroll while the modal is open
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, []);

    const joinAsUser = async () => {
        setJoining(true);
        setError(null);
        try {
            const payload = { email: user.email, source: "modal" };
            if (referredBy) payload.referred_by = referredBy;
            const { data } = await api.post("/waitlist/signup", payload);
            setMeStatus({
                status: data.status,
                position: data.position,
                referral_code: data.referral_code,
                referrals_count: data.referrals_count || 0,
                tier: data.tier,
            });
            clearReferral();
        } catch (err) {
            setError(formatApiError(err, "Couldn't join the waitlist. Try again."));
        } finally {
            setJoining(false);
        }
    };

    const joinAsGuest = async (e) => {
        e.preventDefault();
        if (!guestEmail.trim()) return;
        setJoining(true);
        setError(null);
        try {
            const payload = { email: guestEmail.trim(), source: "modal" };
            if (referredBy) payload.referred_by = referredBy;
            const { data } = await api.post("/waitlist/signup", payload);
            setMeStatus({
                status: data.status,
                position: data.position,
                referral_code: data.referral_code,
                referrals_count: data.referrals_count || 0,
                tier: data.tier,
                _guest: true,
            });
            clearReferral();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setJoining(false);
        }
    };

    const hasLiveStoreLink = TESTFLIGHT_URL || PLAY_STORE_URL;

    return (
        <div
            data-testid="download-app-modal"
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            onClick={onClose}
        >
            <div
                className="relative bg-white border-4 border-black shadow-brutal-lg max-w-md w-full p-6 md:p-8"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    data-testid="download-app-modal-close"
                    aria-label="Close"
                    className="absolute top-3 right-3 border-2 border-black bg-white p-1 shadow-brutal-sm btn-press"
                >
                    <X className="w-4 h-4" />
                </button>

                <p className="inline-block bg-black text-[#CCFF00] border-2 border-black px-2 py-1 font-mono text-[10px] uppercase tracking-widest mb-3">
                    / iOS + Android · Closed beta /
                </p>

                {/* PWA install — skip-the-queue option. Shown only when:
                    - The app is NOT already running standalone
                    - The browser supports installs (Chrome/Edge/Android) OR the user is on iOS Safari */}
                {!_alreadyStandalone && (pwaInstallAvailable || _isIos) && !pwaInstalled && (
                    <div
                        data-testid="pwa-install-banner"
                        className="border-2 border-black bg-[#CCFF00] p-3 mb-4 shadow-brutal-sm"
                    >
                        <div className="flex items-start gap-2 mb-2">
                            <div className="border-2 border-black bg-black text-[#CCFF00] p-1.5 shadow-brutal-sm shrink-0">
                                <Zap className="w-3.5 h-3.5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-mono text-[10px] uppercase tracking-widest leading-none mb-1">/ Skip the wait /</p>
                                <h3 className="font-display text-base font-black uppercase tracking-tighter leading-tight">
                                    Install Fits Me NOW.
                                </h3>
                                <p className="font-mono text-[11px] leading-snug mt-1 text-black/80">
                                    Home-screen icon, stand-alone window, offline browsing. No app-store wait.
                                </p>
                            </div>
                        </div>
                        {pwaInstallAvailable ? (
                            <button
                                onClick={installPwa}
                                data-testid="pwa-install-banner-btn"
                                className="w-full bg-black text-[#CCFF00] border-2 border-black px-3 py-2 font-display font-black text-xs uppercase tracking-widest shadow-brutal-sm btn-press inline-flex items-center justify-center gap-2"
                            >
                                <Smartphone className="w-3.5 h-3.5" /> Install to home screen
                            </button>
                        ) : (
                            <div
                                data-testid="pwa-install-banner-ios-hint"
                                className="border-2 border-black bg-white px-3 py-2 font-mono text-[10px] leading-snug uppercase tracking-widest"
                            >
                                iOS: Tap <span className="bg-black text-[#CCFF00] px-1.5 font-bold">Share</span> &rarr; <strong>Add to Home Screen</strong>
                            </div>
                        )}
                        <p className="font-mono text-[9px] uppercase tracking-widest text-black/60 mt-2">
                            / Still want the native build? Join the waitlist below. /
                        </p>
                    </div>
                )}

                {pwaInstalled && (
                    <div data-testid="pwa-install-banner-success" className="border-2 border-black bg-[#FF00FF] text-white px-3 py-2 mb-4 font-mono text-[11px] uppercase tracking-widest inline-flex items-center gap-2">
                        <Check className="w-3.5 h-3.5" /> Installed — look on your home screen.
                    </div>
                )}

                {/* Referrer banner shows for everyone when ?r=<code> is in the URL */}
                {referrer && !meStatus?.referral_code && (
                    <div data-testid="modal-referrer-banner" className="border-2 border-black bg-[#FF00FF] text-white px-3 py-2 font-mono text-[11px] uppercase tracking-widest mb-3 inline-flex items-center gap-2">
                        <Check className="w-3.5 h-3.5" /> {referrer.first_name} invited you
                    </div>
                )}

                {loading && (
                    <div data-testid="download-app-loading" className="text-center py-8 font-mono text-xs uppercase tracking-widest">
                        Checking your beta status&hellip;
                    </div>
                )}

                {!loading && (
                    <>
                        {/* ── STATE: signed-in + invited (status='invited' or 'installed') ── */}
                        {user && (meStatus?.status === "invited" || meStatus?.status === "installed") && (
                            <div data-testid="download-app-status-invited" className="space-y-4">
                                <h2 className="font-display text-3xl font-black uppercase tracking-tighter leading-none">
                                    YOU&apos;RE IN.<br/>
                                    <span className="bg-[#CCFF00] px-2">DOWNLOAD TIME.</span>
                                </h2>
                                <p className="font-mono text-xs leading-relaxed">
                                    Welcome to the closed beta. Tap your platform below to install.
                                </p>
                                {!hasLiveStoreLink ? (
                                    <div className="border-2 border-black bg-[#FDFBF7] p-3 font-mono text-[11px] uppercase tracking-widest text-center">
                                        Beta build uploading&hellip; we&apos;ll email the link to <strong>{user.email}</strong>.
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {TESTFLIGHT_URL && (
                                            <a
                                                data-testid="download-app-testflight"
                                                href={TESTFLIGHT_URL}
                                                target="_blank"
                                                rel="noreferrer noopener"
                                                className="w-full bg-black text-[#CCFF00] border-2 border-black px-4 py-3 font-display font-black uppercase tracking-widest shadow-brutal-sm btn-press inline-flex items-center justify-center gap-3"
                                            >
                                                <Apple className="w-5 h-5" /> Open in TestFlight
                                                <ExternalLink className="w-3.5 h-3.5 opacity-60" />
                                            </a>
                                        )}
                                        {PLAY_STORE_URL && (
                                            <a
                                                data-testid="download-app-playstore"
                                                href={PLAY_STORE_URL}
                                                target="_blank"
                                                rel="noreferrer noopener"
                                                className="w-full bg-white text-black border-2 border-black px-4 py-3 font-display font-black uppercase tracking-widest shadow-brutal-sm btn-press inline-flex items-center justify-center gap-3"
                                            >
                                                <_GoogleGlyph /> Open in Play Store
                                                <ExternalLink className="w-3.5 h-3.5 opacity-60" />
                                            </a>
                                        )}
                                    </div>
                                )}
                                {meStatus?.referral_code && (
                                    <div className="pt-3 border-t-2 border-black">
                                        <ShareBeta referralCode={meStatus.referral_code} referralsCount={meStatus.referrals_count || 0} tier={meStatus.tier} compact />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── STATE: signed-in + pending ── */}
                        {user && meStatus?.status === "pending" && (
                            <div data-testid="download-app-status-pending" className="space-y-4">
                                <div className="inline-flex items-center justify-center w-14 h-14 border-4 border-black bg-[#CCFF00] shadow-brutal-sm">
                                    <Check className="w-7 h-7" strokeWidth={3} />
                                </div>
                                <h2 className="font-display text-3xl font-black uppercase tracking-tighter leading-none">
                                    YOU&apos;RE ON THE LIST.
                                </h2>
                                <p className="font-mono text-xs leading-relaxed">
                                    Position <span className="bg-[#FF00FF] text-white px-1.5 font-bold">#{meStatus.position}</span> in line.<br/>
                                    We&apos;ll email <strong>{user.email}</strong> the moment your invite is ready (TestFlight or Play Store).
                                </p>
                                {meStatus?.referral_code && (
                                    <div className="pt-3 border-t-2 border-black">
                                        <ShareBeta referralCode={meStatus.referral_code} referralsCount={meStatus.referrals_count || 0} tier={meStatus.tier} />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── STATE: signed-in + NOT on list ── */}
                        {user && meStatus?.status === null && (
                            <div data-testid="download-app-status-join" className="space-y-4">
                                <h2 className="font-display text-3xl font-black uppercase tracking-tighter leading-none">
                                    FITSME<br/>ON YOUR PHONE.
                                </h2>
                                <p className="font-mono text-xs leading-relaxed">
                                    Closed beta launching on TestFlight (iOS) + Google Play (Android). One tap to claim a spot:
                                </p>
                                <button
                                    onClick={joinAsUser}
                                    disabled={joining}
                                    data-testid="download-app-join-as-user"
                                    className="w-full bg-[#CCFF00] text-black border-2 border-black px-4 py-3 font-display font-black uppercase tracking-widest shadow-brutal-sm btn-press disabled:opacity-60 inline-flex items-center justify-center gap-3"
                                >
                                    <Send className="w-4 h-4" />
                                    {joining ? "Adding you…" : `Join as ${user.email.split("@")[0]}`}
                                </button>
                                {error && (
                                    <p data-testid="download-app-error" className="border-2 border-[#FF00FF] bg-white text-[#FF00FF] px-3 py-2 font-mono text-xs uppercase tracking-widest inline-flex items-center gap-2">
                                        <AlertTriangle className="w-3.5 h-3.5" /> {error}
                                    </p>
                                )}
                                <p className="font-mono text-[10px] text-black/60 leading-relaxed border-t-2 border-black pt-3">
                                    No spam. We use this email only to send your TestFlight / Play Closed Testing invite.
                                </p>
                            </div>
                        )}

                        {/* ── STATE: anonymous guest ── */}
                        {!user && meStatus?.status !== "pending" && meStatus?.status !== "invited" && (
                            <div data-testid="download-app-status-guest" className="space-y-4">
                                <h2 className="font-display text-3xl font-black uppercase tracking-tighter leading-none">
                                    FITSME<br/>ON YOUR PHONE.
                                </h2>
                                <p className="font-mono text-xs leading-relaxed">
                                    Invite-only closed beta. Drop your email and we&apos;ll send your tester link, or sign in to claim a spot tied to your account.
                                </p>
                                <form onSubmit={joinAsGuest} data-testid="download-app-guest-form" className="space-y-3">
                                    <input
                                        type="email"
                                        required
                                        autoComplete="email"
                                        placeholder="you@yourcrew.com"
                                        data-testid="download-app-guest-email"
                                        value={guestEmail}
                                        onChange={(e) => setGuestEmail(e.target.value)}
                                        className="w-full border-2 border-black px-3 py-3 font-mono text-sm placeholder:text-black/40 focus:outline-none focus:bg-[#CCFF00]"
                                    />
                                    <button
                                        type="submit"
                                        disabled={joining || !guestEmail.trim()}
                                        data-testid="download-app-guest-submit"
                                        className="w-full bg-black text-[#CCFF00] border-2 border-black px-4 py-3 font-display font-black uppercase tracking-widest shadow-brutal-sm btn-press disabled:opacity-60 inline-flex items-center justify-center gap-2"
                                    >
                                        <Smartphone className="w-4 h-4" /> Get early app access
                                    </button>
                                </form>
                                {error && (
                                    <p data-testid="download-app-error" className="border-2 border-[#FF00FF] bg-white text-[#FF00FF] px-3 py-2 font-mono text-xs uppercase tracking-widest inline-flex items-center gap-2">
                                        <AlertTriangle className="w-3.5 h-3.5" /> {error}
                                    </p>
                                )}
                                <div className="border-t-2 border-black pt-3 flex items-center justify-between gap-2 flex-wrap">
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-black/60">Already a member?</span>
                                    <Link
                                        to="/login"
                                        onClick={onClose}
                                        data-testid="download-app-signin-cta"
                                        className="inline-flex items-center gap-1.5 border-2 border-black bg-[#CCFF00] px-2 py-1 font-mono text-[10px] uppercase tracking-widest shadow-brutal-sm btn-press"
                                    >
                                        <LogIn className="w-3 h-3" /> Sign in
                                    </Link>
                                </div>
                            </div>
                        )}

                        {/* ── STATE: anon but already submitted via guest form (we set _guest flag) ── */}
                        {!user && (meStatus?.status === "pending" || meStatus?.status === "invited") && meStatus?._guest && (
                            <div data-testid="download-app-status-guest-success" className="space-y-4 text-center">
                                <div className="inline-flex items-center justify-center w-14 h-14 border-4 border-black bg-[#CCFF00] shadow-brutal-sm mx-auto">
                                    <Check className="w-7 h-7" strokeWidth={3} />
                                </div>
                                <h2 className="font-display text-3xl font-black uppercase tracking-tighter leading-none">
                                    YOU&apos;RE ON THE LIST.
                                </h2>
                                <p className="font-mono text-xs leading-relaxed">
                                    Position <span className="bg-[#FF00FF] text-white px-1.5 font-bold">#{meStatus.position}</span> in line. We&apos;ll email you when the beta opens.
                                </p>
                                {meStatus?.referral_code && (
                                    <div className="pt-3 border-t-2 border-black text-left">
                                        <ShareBeta referralCode={meStatus.referral_code} referralsCount={meStatus.referrals_count || 0} tier={meStatus.tier} />
                                    </div>
                                )}
                                <Link
                                    to="/login"
                                    onClick={onClose}
                                    data-testid="download-app-signin-after-join"
                                    className="inline-flex items-center gap-2 border-2 border-black bg-white px-3 py-2 font-mono text-xs uppercase tracking-widest shadow-brutal-sm btn-press"
                                >
                                    <LogIn className="w-3.5 h-3.5" /> Sign in to link your spot
                                </Link>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

/**
 * Reusable "Download the app" trigger button. Two visual modes:
 *  variant="pill"  → compact (for navbar)
 *  variant="block" → full-width brutalist (for Profile page card)
 */
export const DownloadAppButton = ({ variant = "pill", className = "", "data-testid": testId }) => {
    const [open, setOpen] = useState(false);

    if (variant === "block") {
        return (
            <>
                <button
                    onClick={() => setOpen(true)}
                    data-testid={testId || "download-app-cta-block"}
                    className={`w-full bg-[#CCFF00] text-black border-2 border-black px-4 py-3 font-display font-black text-sm uppercase tracking-widest inline-flex items-center justify-center gap-2 shadow-brutal-sm btn-press ${className}`}
                >
                    <Smartphone className="w-4 h-4" /> Download the app
                </button>
                {open && <DownloadAppModal onClose={() => setOpen(false)} />}
            </>
        );
    }

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                data-testid={testId || "nav-download-app"}
                aria-label="Download the Fits Me app"
                title="Download the Fits Me app (closed beta)"
                className={`inline-flex items-center gap-1.5 border-2 border-black bg-[#FF00FF] text-white px-3 py-2 shadow-brutal-sm btn-press font-mono text-xs uppercase tracking-widest ${className}`}
            >
                <Smartphone className="w-4 h-4" />
                <span className="hidden lg:inline">Get the app</span>
            </button>
            {open && <DownloadAppModal onClose={() => setOpen(false)} />}
        </>
    );
};
